// ── Background music (themes that have songs) ────────────────────────────────
// One shared player for the whole app. Each theme with a folder in public/assets/audio/music/<theme id>
// has its own playlist; the player plays the current theme's songs in turn (A–Z, Wild West opening
// with Fifty States, then round again). Switching to another theme with music fades one playlist
// out and starts the other. The entry screens (home / new game / theme gallery) have their own
// playlist, "home", which hands over to the table's songs when a game starts. It plays through Web Audio: each song starts a
// little before the previous one ends and they crossfade, so there's no gap or click, and the
// volume works on iOS, where an audio element's volume is fixed. Only the playing song and the
// next one (loaded ~30 s before it's needed) are kept in memory. Music has its own AudioContext and
// gain, separate from the game's sound effects. Preferences stay on this device (localStorage).

import TRACKS from "./music.generated.json";   // { theme id: file names } from public/assets/audio/music, listed by build.mjs

const DIR = "assets/audio/music/";
const FIRST = "Fifty_States_Beneath_the_Stars.mp3";   // Wild West always opens with this one
export const PLAYLISTS: Record<string, string[]> = Object.fromEntries(Object.entries(TRACKS as Record<string, string[]>).map(([id, songs]) =>
  [id, [...songs].sort((a, b) => Number(b === FIRST) - Number(a === FIRST) || a.localeCompare(b))]));
/** Every theme's songs together ("theme/file"), for tables whose theme has none of its own. */
export const MIX = "mix";
PLAYLISTS[MIX] = Object.entries(PLAYLISTS).filter(([id]) => id !== "home").flatMap(([id, songs]) => songs.map((f) => `${id}/${f}`)).sort((a, b) => a.split("/")[1].localeCompare(b.split("/")[1]));
export const hasMusic = (themeId: string): boolean => !!PLAYLISTS[themeId]?.length;
/** A theme's own songs, or the mix when it has none. */
const playlistFor = (themeId: string): string => (hasMusic(themeId) ? themeId : MIX);
/** The song's file name without its folder. */
const bare = (entry: string): string => entry.split("/").pop()!;
const PRELOAD = 30;         // seconds before a song ends that the next one starts loading
const PREF_KEY = "poli-music";
const XFADE = 2.5;          // seconds the end of one song overlaps the start of the next
const FADE_IN = 0.4;        // the next song comes in quickly: the songs open at full strength over the old one's tail
const FADE = 1.2;           // seconds for fading in/out when the theme changes or the app hides
const HUSH_FADE = 3;        // the slower fade when the game is over and its results pop up

interface Prefs { volume: number; muted: boolean; last: number }   // volume 0..1 (kept while muted); last = last non-zero volume

function loadPrefs(): Prefs {
  try {
    const p = JSON.parse(localStorage.getItem(PREF_KEY) ?? "null");
    if (p && typeof p.volume === "number") {
      const volume = Math.min(1, Math.max(0, p.volume));
      return { volume, muted: !!p.muted, last: typeof p.last === "number" && p.last > 0 ? Math.min(1, p.last) : volume || 0.15 };
    }
  } catch { /* storage unavailable or corrupt */ }
  return { volume: 0.15, muted: false, last: 0.15 };
}

interface Track { buf: AudioBuffer; start: number; end: number }   // start/end: the song without its silent edges

class MusicPlayer {
  prefs = loadPrefs();
  private ctx?: AudioContext;
  private master?: GainNode;
  private loading = new Map<number, Promise<Track | null>>();   // by playlist index
  private loaded = new Map<number, Track>();
  private bad = new Set<number>();          // songs that couldn't be loaded — skipped
  private cur = -1;                         // playlist index of the latest scheduled song
  missing = false;                          // no song in the current playlist could be loaded
  private list = "";                        // theme whose playlist is loaded / playing
  private started = false;                 // a player interaction has started the game
  private theme = "";
  private hushed = false;                 // game over: faded out, the player's volume left as it was
  private scene = "";                      // "home" on the entry screens (home / new game / gallery): its own playlist
  private nodes: { src: AudioBufferSourceNode; gain: GainNode; start: number; end: number; file: string }[] = [];
  private nextAt = 0;                      // context time the next song starts
  private timer = 0;
  private pauseTok = 0;
  private passCount = 0;                   // songs started so far (diagnostics)
  onChange?: () => void;

  get audible(): boolean { return !this.prefs.muted && this.prefs.volume > 0; }
  /** A playlist the player picked from the music panel (null: follow the screen and the table theme). */
  private pick: string | null = null;
  /** Where to start the next playlist (a song chosen from the list), else from the top. */
  private startAt = -1;
  /** The playlist wanted now: the one the player picked, else the entry screens' own, else the table theme's (or the mix). */
  private get want(): string { return this.pick ?? (this.scene || playlistFor(this.theme)); }
  private get shouldPlay(): boolean {
    return this.started && !this.hushed && hasMusic(this.want) && this.audible && !(this.missing && this.list === this.want) && document.visibilityState === "visible";
  }
  private get tracks(): string[] { return PLAYLISTS[this.list] ?? []; }

  /** Call from a click/tap handler (e.g. Start Game) — browsers only allow audio after one. */
  start(): void { this.started = true; this.unlock(); this.update(); }
  setTheme(id: string): void { if (id === this.theme) return; this.theme = id; this.update(); }
  /** "home" while the entry screens are showing, "" in the game. Changing it crossfades (or fades out). */
  setScene(id: string): void { if (id === this.scene) return; this.scene = id; this.update(); }
  /** Fade the music out gently (true) or bring it back (false), without touching the volume prefs. */
  hush(on: boolean): void { if (on === this.hushed) return; this.hushed = on; this.update(on ? HUSH_FADE : FADE); }
  /** Has the player interacted yet (so music may play)? */
  get isStarted(): boolean { return this.started; }
  /** true once sound can actually come out (the browser hasn't blocked autoplay) */
  get isRunning(): boolean { return this.ctx?.state === "running"; }

  setVolume(v: number): void {
    v = Math.min(1, Math.max(0, v));
    // above zero turns music on at that level; zero silences it (unmute then restores `last`)
    this.prefs = v > 0 ? { volume: v, muted: false, last: v } : { ...this.prefs, volume: 0, muted: false };
    this.hushed = false; this.save(); this.update(0.08);
  }
  toggleMute(): void {
    if (!this.audible) this.prefs = { volume: this.prefs.volume || this.prefs.last || 0.15, muted: false, last: this.prefs.volume || this.prefs.last || 0.15 };
    else this.prefs = { ...this.prefs, muted: true };
    this.hushed = false; this.save(); this.update(0.08);
  }

  private save(): void { try { localStorage.setItem(PREF_KEY, JSON.stringify(this.prefs)); } catch { /* ignore */ } this.onChange?.(); }
  private target(): number { return this.audible ? this.prefs.volume ** 2 : 0; }   // squared: the slider feels even to the ear

  private unlock(): void {
    try {
      if (!this.ctx) {
        const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
        this.ctx = new Ctx();
        this.master = this.ctx.createGain();
        this.master.gain.value = 0;
        this.master.connect(this.ctx.destination);
      }
      // resume inside the click that called us (iOS only lets a context start from a gesture);
      // update() suspends it again straight away if music isn't wanted yet
      if (this.ctx.state === "suspended") void this.ctx.resume().catch(() => {});
    } catch { this.missing = true; }
  }

  private loadTrack(i: number): Promise<Track | null> {
    let p = this.loading.get(i);
    if (!p) {
      p = (async () => {
        try {
          const entry = this.tracks[i];
          const path = entry.includes("/") ? entry.split("/").map(encodeURIComponent).join("/") : this.list + "/" + encodeURIComponent(entry);
          const res = await fetch(DIR + path);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data = await res.arrayBuffer();
          const buf = await new Promise<AudioBuffer>((ok, bad) => this.ctx!.decodeAudioData(data, ok, bad));   // callback form for older Safari
          const t = { buf, ...silenceBounds(buf) };
          this.loaded.set(i, t);
          return t;
        } catch (e) {
          console.warn(`[music] ${this.tracks[i]} not available:`, e);
          this.bad.add(i); this.loading.delete(i);
          return null;
        }
      })();
      this.loading.set(i, p);
    }
    return p;
  }
  /** The song after `i` that hasn't failed to load (may be `i` itself with a one-song playlist). */
  private after(i: number): number {
    const n = this.tracks.length;
    for (let k = 1; k <= n; k++) { const j = (i + k) % n; if (!this.bad.has(j)) return j; }
    return -1;
  }
  /** The first song that loads, starting from `from`; marks the music missing if none does. */
  private async firstPlayable(from: number): Promise<number> {
    const n = this.tracks.length;
    for (let k = 0; k < n; k++) {
      const i = (from + k) % n;
      if (!this.bad.has(i) && await this.loadTrack(i)) return i;
    }
    this.missing = true; queueMicrotask(() => this.update());   // switch the idle context off
    return -1;
  }

  private applyGain(secs: number): void {
    if (!this.ctx || !this.master) return;
    const g = this.master.gain, now = this.ctx.currentTime;
    g.cancelScheduledValues(now); g.setValueAtTime(g.value, now);
    g.linearRampToValueAtTime(this.shouldPlay ? this.target() : 0, now + secs);
  }

  /** Bring playback in line with theme / prefs / visibility. Safe to call any number of times.
   *  `secs`: how long the volume takes to get there (a slider move applies at once; a theme change fades). */
  update(secs = FADE): void {
    this.onChange?.();
    if (!this.started) return;
    if (this.shouldPlay) {
      this.unlock();
      const ctx = this.ctx; if (!ctx) return;
      const tok = ++this.pauseTok;
      if (this.list !== this.want) {
        // another theme's songs are loaded: fade them out (if they're playing), drop them, then start this theme's
        if (this.nodes.length && ctx.state === "running") {
          const out = FADE * 0.8, g = this.master!.gain, now = ctx.currentTime;
          g.cancelScheduledValues(now); g.setValueAtTime(g.value, now); g.linearRampToValueAtTime(0, now + out);
          setTimeout(() => { if (tok === this.pauseTok) { this.switchTo(this.want); this.update(secs); } }, out * 1000 + 60);
          return;
        }
        this.switchTo(this.want);
      }
      // already playing (paused in place): carry on; otherwise start the playlist
      const from = this.startAt >= 0 ? this.startAt : Math.max(0, this.cur);
      this.startAt = -1;
      const ready = this.nodes.length ? Promise.resolve(this.cur) : this.firstPlayable(from);
      void ready.then((i) => {
        if (i < 0 || tok !== this.pauseTok || !this.shouldPlay) return;
        void ctx.resume().catch(() => {}).then(() => {
          if (tok !== this.pauseTok) return;
          if (!this.nodes.length) this.play(i, this.loaded.get(i)!, ctx.currentTime + 0.05, true);
          this.applyGain(secs);
          clearInterval(this.timer); this.timer = window.setInterval(() => this.tick(), 1000);
        });
      });
    } else if (this.ctx && this.ctx.state === "running") {
      // fade out, then freeze the context: the track keeps its place for when it comes back
      const out = secs > FADE ? secs : Math.min(secs, FADE * 0.8);   // a deliberately long fade (hush) keeps its length
      this.applyGain(out);
      const tok = ++this.pauseTok;
      setTimeout(() => { if (tok === this.pauseTok && !this.shouldPlay) { clearInterval(this.timer); void this.ctx?.suspend().catch(() => {}); } }, out * 1000 + 60);
    }
  }

  private switchTo(theme: string): void {
    for (const n of this.nodes) { try { n.src.stop(); } catch { /* already stopped */ } }
    this.nodes = []; this.loaded.clear(); this.loading.clear(); this.bad.clear();
    this.cur = -1; this.nextAt = 0; this.missing = false; this.list = theme;
    clearInterval(this.timer);
  }

  // Once a second: load the next song in good time, and queue it to start as this one ends.
  // Context time stops while suspended, so the schedule never drifts.
  private tick(): void {
    const ctx = this.ctx; if (!ctx) return;
    const now = ctx.currentTime;
    this.nodes = this.nodes.filter((n) => n.end > now - 1);
    const next = this.after(this.cur);
    if (next < 0 || this.nextAt - now > PRELOAD) return;
    const t = this.loaded.get(next);
    if (!t) { void this.loadTrack(next); return; }        // fetching; a failure moves on to the one after
    if (this.nextAt - now > 6) return;
    this.play(next, t, Math.max(this.nextAt, now + 0.05), false);
  }

  private play(i: number, t: Track, at: number, first: boolean): void {
    const ctx = this.ctx; if (!ctx || !this.master) return;
    const len = t.end - t.start;
    const x = Math.min(XFADE, len / 4);
    const src = ctx.createBufferSource(); src.buffer = t.buf;
    const gain = ctx.createGain();
    src.connect(gain).connect(this.master);
    // equal-power curves: a quick fade-in (not on the very first song), and the old song fades out over the overlap
    const up = new Float32Array(32).map((_, k) => Math.sin((k / 31) * Math.PI / 2));
    const down = up.slice().reverse();
    if (first) gain.gain.setValueAtTime(1, at); else gain.gain.setValueCurveAtTime(up, at, Math.min(FADE_IN, x));
    gain.gain.setValueCurveAtTime(down, at + len - x, x);
    src.start(at, t.start, len);
    this.nodes.push({ src, gain, start: at, end: at + len, file: bare(this.tracks[i]) }); this.passCount++;
    this.cur = i; this.nextAt = at + len - x;
    // keep only this song decoded (the one ending is held by its own source until it finishes)
    for (const k of [...this.loaded.keys()]) if (k !== i) { this.loaded.delete(k); this.loading.delete(k); }
  }

  /** File name of the song you're hearing (the latest one that has started), else the one that
   *  will play first for the current theme. Null when the theme has no music. */
  nowPlaying(): string | null {
    if (this.list === this.want && this.ctx) {
      const now = this.ctx.currentTime;
      const on = this.nodes.filter((n) => n.start <= now + 0.05).pop();
      if (on) return on.file;
    }
    const first = PLAYLISTS[this.want]?.[0];
    return first ? bare(first) : null;
  }

  /** The songs you can choose from: each theme's, with the theme's id. */
  songs(): { theme: string; file: string }[] {
    return Object.entries(PLAYLISTS).filter(([id]) => id !== MIX).flatMap(([theme, songs]) => songs.map((file) => ({ theme, file })));
  }
  /** True while a song picked from the list is playing (not following the table theme). */
  get picked(): boolean { return this.pick !== null; }
  /** Play this song now, then carry on through its theme's songs. */
  playSong(theme: string, file: string): void {
    const i = (PLAYLISTS[theme] ?? []).indexOf(file);
    if (i < 0) return;
    this.pick = theme;
    this.switchTo(theme);
    this.startAt = i;
    this.start();
  }
  /** Back to the screen's / table theme's music. */
  followTheme(): void { if (this.pick === null) return; this.pick = null; this.update(); }

  /** Testing hook: what the player is doing right now. */
  debug() { return { theme: this.theme, scene: this.scene, prefs: this.prefs, missing: this.missing, ctx: this.ctx?.state, gain: this.master?.gain.value, passes: this.nodes.length, started: this.passCount, isStarted: this.started, list: this.list, song: this.tracks[this.cur], hearing: this.nowPlaying(), nextIn: this.ctx ? +(this.nextAt - this.ctx.currentTime).toFixed(1) : null, decoded: this.loaded.size }; }
}

// Leading/trailing near-silence (Suno tracks often have some) would leave a gap between songs.
function silenceBounds(buf: AudioBuffer): { start: number; end: number } {
  const d = buf.getChannelData(0), n = d.length, thr = 0.003;
  let a = 0; while (a < n && Math.abs(d[a]) < thr) a++;
  let b = n - 1; while (b > a && Math.abs(d[b]) < thr) b--;
  if (b - a < buf.sampleRate) return { start: 0, end: buf.duration };
  return { start: a / buf.sampleRate, end: (b + 1) / buf.sampleRate };
}

export const music = new MusicPlayer();

// Follow the page's theme (set on <body data-theme> by applyTheme) and the app going to the background.
export function initMusic(): void {
  const sync = () => music.setTheme(document.body.dataset.theme ?? "");
  new MutationObserver(sync).observe(document.body, { attributes: true, attributeFilter: ["data-theme"] });
  sync();
  document.addEventListener("visibilitychange", () => music.update());
  window.addEventListener("pagehide", () => music.update());
  (globalThis as unknown as { __poliMusic: MusicPlayer }).__poliMusic = music;
}
